/**
 * Video upload core: the clip slot, the presigned PUTs, the upload confirmation and the cancel.
 * The endpoints are POST /v1/video, POST /v1/video/{id}/uploaded and DELETE /v1/video/{id}.
 */

import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { sendXhr } from './xhr-send.js';

/** Mirrors Video::CONTEXT_MESSAGE. */
export const MESSAGE_CONTEXT = 'message';

/**
 * A failed video request. `code` is the API error code, or one of `network`, `aborted` and `http`
 * when the response carries none.
 */
export class VideoUploadError extends Error {
    /**
     * @param {string} code
     * @param {number|null} [status]
     */
    constructor(code, status = null) {
        super(code);
        this.name = 'VideoUploadError';
        this.code = code;
        this.status = status;
    }
}

/**
 * @param {Response} response
 * @returns {Promise<VideoUploadError>}
 */
async function responseError(response) {
    let code = 'http';
    try {
        const body = await response.json();
        if (typeof body?.error?.code === 'string' && body.error.code !== '') {
            code = body.error.code;
        }
    } catch {
        // A proxy error page is not JSON. The status still names the failure.
    }
    return new VideoUploadError(code, response.status);
}

/**
 * @param {string} endpoint
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
async function request(endpoint, options) {
    // Mints the JWT when the page has none, so the client sends the header.
    await getApiToken();
    let response;
    try {
        response = await apiClient.request(endpoint, options);
    } catch {
        throw new VideoUploadError('network');
    }
    if (!response.ok) {
        throw await responseError(response);
    }
    return response;
}

/**
 * Reserve the upload slot. A repeat with the same client key returns the same slot.
 *
 * @param {{clientKey: string, durationMs: number, bytes: number}} clip
 * @returns {Promise<{id: string, status: string, upload?: {video: UploadTarget, poster: UploadTarget}}>}
 */
export async function createVideoSlot({ clientKey, durationMs, bytes }) {
    const body = new URLSearchParams({
        context: MESSAGE_CONTEXT,
        client_key: clientKey,
        duration_ms: String(durationMs),
        bytes: String(bytes),
    });
    const response = await request('/video', { method: 'POST', body });
    const slot = await response.json();
    if (!slot || typeof slot.id !== 'string' || slot.id === '') {
        throw new VideoUploadError('http', response.status);
    }
    return slot;
}

/**
 * @typedef {{url: string, method: string, headers: Object<string, string>}} UploadTarget
 */

/**
 * PUT a blob to a presigned URL. The request sends the target headers exactly, because the signature covers them.
 *
 * @param {UploadTarget} target
 * @param {Blob} blob
 * @param {{onProgress?: function(number): void, signal?: AbortSignal}} [options]
 * @returns {Promise<void>}
 */
export async function putUploadTarget(target, blob, { onProgress, signal } = {}) {
    let xhr;
    try {
        xhr = await sendXhr({
            method: target.method || 'PUT',
            url: target.url,
            headers: target.headers || {},
            body: blob,
            signal,
            onProgress,
        });
    } catch (error) {
        throw new VideoUploadError(error.kind === 'aborted' ? 'aborted' : 'network');
    }
    if (xhr.status < 200 || xhr.status >= 300) {
        throw new VideoUploadError('http', xhr.status);
    }
}

/**
 * Confirm both objects are in storage. The clip then moves to its check.
 *
 * @param {string} videoId
 * @returns {Promise<{id: string, status: string}>}
 */
export async function markVideoUploaded(videoId) {
    const response = await request(`/video/${encodeURIComponent(videoId)}/uploaded`, { method: 'POST' });
    return response.json();
}

/**
 * Cancel a clip that no message holds. The server deletes its objects.
 *
 * @param {string} videoId
 * @returns {Promise<void>}
 */
export async function cancelVideo(videoId) {
    await request(`/video/${encodeURIComponent(videoId)}`, { method: 'DELETE' });
}
